/* global G */
import { asyncPipeSpread, setKey } from 'lib/util'
import { get } from 'lib/sequence/component/state/value'
import find from 'lib/sequence/component/children/find'
import { setData } from 'lib/sequence/model/api/set'
import { hidden } from 'lib/sequence/component/state/hidden'
import { checked } from 'lib/sequence/component/state/checked'

/**
 * Sets the `module`'s state as error if any of the model's sub-models or the model itself
 * has error state.
 *
 * @param {string} [attribute]  sub-model to use
 * @returns {function(*): function(*, ...[*]): Promise<*>}
 */
const updateModuleState = attribute => module => async (components, ...args) => {
  const model = attribute
    ? module[G.MODEL][G.CHILDREN][attribute]
    : module[G.MODEL]

  const validationError = model[G.STATE][G.ERROR]
  const moduleState = module[G.STATE]
  moduleState[G.ERROR] = !!validationError

  return [components, ...args]
}

/**
 * Fills required data.
 *
 * TODO: Temporarily added. Replace with proper implementation of RadioButtons & DatePicker in the
 *        mobile version.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setEmptyData = module => async (components, ...args) => {
  const intl = module[G.ADAPTER][G.INTL]
  const model = module[G.MODEL]
  const { installationReport } = model[G.CHILDREN]
  const { installerIs } = components

  hidden(installerIs) && setData(installationReport, {
    installerName: await intl._t(
      'label.unknown',
      {
        ns: 'common',
        _key: 'label.unknown',
        defaultValue: 'Unknown',
      },
    ),
    installerIs: 'me',
  })

  return [components, ...args]
}

/**
 * Sets the equipment ref
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setEquipment = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { equipment: parent, installationReport } = model[G.CHILDREN]
  const { equipment } = installationReport[G.CHILDREN]

  setKey(parent[G.STATE][G.REF], G.REF, equipment[G.STATE])

  return [components, ...args]
}

/**
 * Sets values for the `installationDate` and `installationDateUnknown` model attributes as needed.
 * TODO: This is actually a workaround to overcome some framework limitations.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setDate = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { installationReport } = model[G.CHILDREN]
  const { installationDateUnknown } = components

  if (checked(installationDateUnknown)) {
    setData(installationReport, { installationDate: null })
  } else {
    setData(installationReport, { installationDateUnknown: false })
  }
  return [components, ...args]
}

/**
 * Sets the serial for the installation report
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setSerial = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { installationReport } = model[G.CHILDREN]
  const { serial: serialField } = components

  const serialValue = get(serialField)

  serialValue && setData(installationReport, { serial: serialValue })

  return [components, ...args]
}

/**
 * Sets the status for the installation report
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setStatus = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { installationReport } = model[G.CHILDREN]

  setData(installationReport, { status: 10 })

  return args
}

/**
 * EquipmentInformation Installation Report Create Action
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*): function(...[*]): Promise<*[]>}
 */
export default module => () => async (...args) => asyncPipeSpread(
  updateModuleState('installationReport')(module),
  setEmptyData(module),
  setEquipment(module),
  setDate(module),
  setSerial(module),
  setStatus(module),
)(find(module[G.STATE][G.ACTION][G.COMPONENT]), ...args)
